import { commitFaceAccepted, handBack, record, upper, type Row } from '../ability/context.js';
import type { TurnTickResult } from './ticks.js';
import saveShape from '../../../catalogs/janus/tick-save-shape.json';

const uuid = (v: unknown): v is string => typeof v === 'string' && /^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/i.test(v);
const integer = (v: unknown, lo = -1000000, hi = 1000000): boolean => Number.isSafeInteger(v) && Number(v) >= lo && Number(v) <= hi;
const finite = (v: unknown): boolean => typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= 1000000;
const text = (v: unknown, max: number): v is string => typeof v === 'string' && v.length <= max;
const reason = (v: unknown): boolean => v === null || text(v, 40);
const fail = (part: string): never => handBack('turn-tick:contract:' + part);
const columns = (row: Row, keys: readonly string[], part: string): void => {
  if (Object.keys(row).some(key => !keys.includes(key))) fail(part + '-columns');
};

/** Browser-only value contract, shared by turn dry runs, elapsed time and hunger consequences.
 * New status ids and charge preimages may be assigned by the caller after the synthetic phase. */
export function assertTurnTickContract(tick: TurnTickResult, maxRows = 32768): void {
  if (!record(tick) || !record(tick.ec)) fail('shape');
  for (const rows of [tick.rows, tick.statusesAdded, tick.statusesRemoved, tick.statusesChanged, tick.charges,
    tick.radiusChanges ?? []]) if (!Array.isArray(rows)) fail('shape');
  if (tick.statusFrames !== undefined && !record(tick.statusFrames)) fail('status-frames');
  const statusMax = maxRows === 64 ? 8 : maxRows;
  if (tick.rows.length > maxRows || tick.statusesAdded.length > statusMax || tick.statusesRemoved.length > statusMax
    || tick.statusesChanged.length > statusMax || tick.charges.length > (maxRows === 64 ? 16 : maxRows)
    || (tick.radiusChanges?.length ?? 0) > (maxRows === 64 ? 1 : maxRows)
    || Object.keys(tick.statusFrames ?? {}).length > maxRows) fail('capacity');
  for (const row of tick.rows) {
    if (!record(row)) fail('log');
    columns(row as unknown as Row, ['roll_type_1', 'roll_type_2', 'roll_item_id', 'roll_by', 'roll_to', 'roll_result', 'rolls_full', 'dice', 'roll_private', 'feedback', 'item_display', 'log_item_target', 'random_table', 'house_rule'], 'log');
    if (!text(row.roll_type_1, 120) || !row.roll_type_1 || row.roll_type_2 != null && !text(row.roll_type_2, 120)
      || !integer(row.roll_result)) fail('log');
    for (const id of [row.roll_item_id, row.roll_by, row.roll_to]) if (id != null && !uuid(id)) fail('log-identity');
    if (row.roll_private !== undefined && ![0, 1, 2].includes(row.roll_private)) fail('log-privacy');
    const faces = row.rolls_full;
    if (typeof faces === 'string' && row.roll_type_1 === 'janus_unported_skip') {
      if (!text(faces, 65536)) fail('skip-text');
    } else if (faces != null) {
      if (!Array.isArray(faces) || faces.length > 256) fail('faces');
      for (const face of faces as unknown[]) {
        if (!Array.isArray(face) || face.length < 2 || face.length > 3 || !commitFaceAccepted(face[0]) || !text(face[1], 80)) fail('face');
        const meta = (face as unknown[])[2];
        if (meta != null) {
          const values = record(meta);
          if (!values || Object.keys(values).length > 16) fail('face-meta');
          for (const [key, value] of Object.entries(values!)) if (!/^[a-z_]{1,40}$/.test(key)
            || value !== null && !['string', 'number', 'boolean'].includes(typeof value)
            || typeof value === 'string' && value.length > 200) fail('face-meta');
        }
      }
    }
    if (row.dice) {
      columns(row.dice, ['dice', 'die', 'mod', 'adv_dis_stacks', 'dc'], 'dice');
      for (const [key, lo, hi] of [['dice', 0, 100], ['die', 0, 1000], ['mod', -10000, 10000], ['adv_dis_stacks', -100, 100], ['dc', 0, 100000]] as const)
        if (!integer(row.dice[key] ?? 0, lo, hi)) fail('dice-' + key);
    }
    const fb = row.feedback;
    if (fb != null) {
      if (!record(fb)) fail('feedback');
      if (!integer(fb.rolled, 0) || !integer(fb.applied, 0) || !reason(fb.reason)) fail('feedback');
      if (['restored', 'gained', 'spent'].includes(fb.verb)) {
        columns(fb as unknown as Row, ['rolled', 'applied', 'verb', 'reason'], 'feedback');
      } else {
        columns(fb as unknown as Row, ['rolled', 'applied', 'verb', 'reason', 'damage_types'], 'feedback');
        if (fb.verb !== 'taken' || !['82BBB3B6-4646-48E8-A5A2-8DC0947A91D0', '77A044B7-7041-CA96-3C63-BB718B15A364'].includes(row.roll_type_1)
          || fb.applied !== row.roll_result) fail('feedback-damage');
        const types = new Set([row.roll_type_2, ...(Array.isArray(faces) ? faces.map(face => Array.isArray(face) ? record(face[2])?.damage_type_id : null) : [])]
          .filter(uuid).map(upper));
        if (fb.damage_types) {
          if (!Array.isArray(fb.damage_types) || !fb.damage_types.length || fb.damage_types.length > 16) fail('feedback-types');
          for (const part of fb.damage_types) {
            if (!record(part)) fail('feedback-type');
            columns(part as unknown as Row, ['damage_type_id', 'rolled', 'applied', 'typed', 'reason'], 'feedback-type');
            if (!uuid(part.damage_type_id) || !types.has(upper(part.damage_type_id)) || !integer(part.rolled, 0) || !integer(part.typed, 0)
              || part.applied !== null && !integer(part.applied, 0) || !reason(part.reason)) fail('feedback-type');
          }
          if (fb.damage_types.every(part => part.applied !== null) && fb.damage_types.reduce((sum, part) => sum + part.applied!, 0) !== row.roll_result) fail('feedback-sum');
        }
      }
    }
  }
  const creatureIds = new Set<string>();
  for (const [id, changes] of Object.entries(tick.ec)) {
    if (!uuid(id) || !record(changes) || Object.keys(changes).length > 12) fail('creature');
    if (creatureIds.has(upper(id))) fail('creature-duplicate');
    creatureIds.add(upper(id));
    for (const [key, value] of Object.entries(changes)) {
      const type = saveShape.ec[key as keyof typeof saveShape.ec];
      if (!type) fail('creature-column:' + key);
      if (type === 'queue') {
        if (value === null) continue;
        if (!text(value, 256)) fail('orb-queue');
        let queue: unknown; try { queue = JSON.parse(value as string); } catch { fail('orb-queue'); }
        if (!Array.isArray(queue) || queue.some(slug => typeof slug !== 'string')) fail('orb-queue');
      } else if (type === 'uuid' ? !uuid(value) : type === 'float' ? !finite(value) : !integer(value)) fail('creature-value:' + key);
    }
  }
  for (const status of tick.statusesAdded) {
    if (!record(status)) fail('status');
    columns(status as unknown as Row, ['status_id', 'status_item_id', 'target', 'caster', 'status_connected_id', 'status_stack', 'ends',
      'status_ends_on_target_start_of_turn', 'status_ends_on_target_end_of_turn', 'status_ends_on_caster_start_of_next_turn', 'status_ends_on_caster_end_of_next_turn',
      'status_aoe_type', 'status_aoe_distance', 'status_aoe_distance_depth', 'status_aoe_distance_height', 'status_aoe_source', 'status_origin_point_x',
      'status_origin_point_y', 'status_origin_point_z', 'status_distance_point_x', 'status_distance_point_y', 'status_distance_point_z', 'status_origin_id',
      'status_casting_targets_arr', 'status_opposing_team_only', 'status_damage_type', 'status_forced_roll_extreme'], 'status');
    for (const id of [status.status_item_id, status.target, status.caster, status.status_connected_id, ...(status.status_id === undefined ? [] : [status.status_id])])
      if (!uuid(id)) fail('status-identity');
    if (!integer(status.status_stack, 1, 10000) || !/^\d{1,9}(?:-\d{1,3}){5}$/.test(status.ends)) fail('status');
    for (const flag of [status.status_ends_on_target_start_of_turn, status.status_ends_on_target_end_of_turn, status.status_ends_on_caster_start_of_next_turn, status.status_ends_on_caster_end_of_next_turn])
      if (!integer(flag, 0, 1)) fail('status-end');
    for (const value of [status.status_aoe_type, status.status_origin_id, status.status_damage_type]) if (value != null && !uuid(value)) fail('status-optional-identity');
    for (const value of [status.status_aoe_distance, status.status_aoe_distance_depth, status.status_aoe_distance_height, status.status_origin_point_x, status.status_origin_point_y,
      status.status_origin_point_z, status.status_distance_point_x, status.status_distance_point_y, status.status_distance_point_z]) if (value !== undefined && !finite(value)) fail('status-coordinate');
    if (status.status_aoe_source !== undefined && !integer(status.status_aoe_source, 0, 1)
      || status.status_opposing_team_only !== undefined && !integer(status.status_opposing_team_only, 0, 2)
      || status.status_forced_roll_extreme !== undefined && ![-1, 0, 1].includes(status.status_forced_roll_extreme)
      || status.status_casting_targets_arr != null && !text(status.status_casting_targets_arr, 16384)) fail('status-value');
  }
  for (const status of tick.statusesRemoved) {
    if (!record(status)) fail('status-removal');
    columns(status as unknown as Row, ['status_id', 'status_item_id', 'target', 'caster', 'before'], 'status-removal');
    if (!uuid(status.status_item_id) || !uuid(status.target) || status.status_id !== undefined
      && (!uuid(status.status_id) || !uuid(status.caster) || !record(status.before))) fail('status-removal');
  }
  for (const status of tick.statusesChanged) {
    if (!record(status)) fail('status-stack');
    columns(status as unknown as Row, ['status_id', 'status_stack'], 'status-stack');
    if (!uuid(status.status_id) || !integer(status.status_stack, 0, 10000)) fail('status-stack');
  }
  for (const change of tick.radiusChanges ?? []) {
    if (!record(change)) fail('radius');
    columns(change as unknown as Row, ['kind', 'status_id', 'before', 'status_aoe_distance'], 'radius');
    if (!uuid(change.status_id) || change.kind !== 'class_aura_radius'
      || !finite(change.before) || change.before < 0 || !finite(change.status_aoe_distance) || change.status_aoe_distance < 0) fail('radius');
  }
  for (const charge of tick.charges) {
    if (!record(charge)) fail('charge');
    columns(charge as unknown as Row, ['holder', 'item_id', 'before', 'charges'], 'charge');
    if (!uuid(charge.holder) || !uuid(charge.item_id) || !integer(charge.charges, 0)) fail('charge');
    if ('before' in charge && !integer(charge.before, 0)) fail('charge-before');
  }
  for (const [id, frame] of Object.entries(tick.statusFrames ?? {})) if (!uuid(id) || !record(frame) || !Array.isArray(frame.status_remove_ids)
    || frame.status_remove_ids.some(sid => !uuid(sid)) || !record(frame.skills) || frame.defenses !== undefined && !record(frame.defenses)) fail('status-frame');
}
